import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../components/BookingModal'

export default function Destinations() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedDestination, setSelectedDestination] = useState('')
  const [selectedRegion, setSelectedRegion] = useState(null)
  const [destinations, setDestinations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const handleBooking = (destination) => {
    setSelectedDestination(destination)
    setIsModalOpen(true)
  }

  // Hardcoded destinations as fallback
  const hardcodedDestinations = [
    {
      name: 'Bwindi Impenetrable National Park',
      region: 'Southwestern Uganda',
      category: 'Mountain Uganda',
      description: 'Ancient rainforest home to half the world\'s mountain gorillas. Trek through misty forests for unforgettable encounters with these gentle giants.',
      image: '/images/bwindi-hero.jpg',
      activities: ['Gorilla Trekking', 'Birdwatching', 'Nature Walks'],
      bestTime: 'June-Sept, Dec-Feb',
      duration: '2-3 days'
    },
    {
      name: 'Jinja',
      region: 'Eastern Uganda',
      category: 'Adventure Uganda',
      description: 'The adventure capital of East Africa and source of the mighty Nile River. Experience world-class white-water rafting and adrenaline activities.',
      image: '/images/jinja-hero.jpg',
      activities: ['White Water Rafting', 'Kayaking', 'Bungee Jumping'],
      bestTime: 'Year-round',
      duration: '1-2 days'
    },
    {
      name: 'Kibale National Park',
      region: 'Western Uganda',
      category: 'Primate Uganda',
      description: 'The primate capital of the world with the highest concentration of primates in East Africa, including over 1,500 chimpanzees.',
      image: '/images/kibale-hero.jpg',
      activities: ['Chimp Tracking', 'Forest Walks', 'Birdwatching'],
      bestTime: 'Feb-Jun, Sept-Nov',
      duration: '1-2 days'
    },
    {
      name: 'Kidepo Valley National Park',
      region: 'Northern Uganda',
      category: 'Wilderness Uganda',
      description: 'Uganda\'s most isolated park offering true wilderness experience with dramatic scenery and rare wildlife species.',
      image: '/images/kidepo-hero.jpg',
      activities: ['Big Game Viewing', 'Cultural Encounters', 'Wildlife Photography'],
      bestTime: 'Sept-March',
      duration: '2-3 days'
    },
    {
      name: 'Lake Bunyonyi',
      region: 'Southwestern Uganda',
      category: 'Relaxation Uganda',
      description: 'Africa\'s second-deepest lake dotted with 29 islands. Perfect for relaxation, water activities, and stunning scenic beauty.',
      image: '/images/lake-bunyonyi-hero.jpg',
      activities: ['Boat Cruises', 'Island Hopping', 'Relaxation'],
      bestTime: 'Year-round',
      duration: '1-2 days'
    },
    {
      name: 'Lake Mburo National Park',
      region: 'Western Uganda',
      category: 'Savanna Uganda',
      description: 'Uganda\'s smallest savanna park offering intimate safari experiences with walking safaris and diverse wildlife.',
      image: '/images/lake-mburo-hero.jpg',
      activities: ['Walking Safaris', 'Boat Cruises', 'Horse Riding'],
      bestTime: 'Jun-Sept, Dec-Feb',
      duration: '1-2 days'
    },
    {
      name: 'Mount Elgon National Park',
      region: 'Eastern Uganda',
      category: 'Mountain Uganda',
      description: 'Ancient extinct volcano with the world\'s largest intact caldera. Home to spectacular waterfalls and diverse ecosystems.',
      image: '/images/mount-elgon-hero.jpg',
      activities: ['Mountain Trekking', 'Cave Exploration', 'Waterfall Tours'],
      bestTime: 'Dec-Feb, Jun-Sept',
      duration: '3-6 days'
    },
    {
      name: 'Murchison Falls National Park',
      region: 'Northwestern Uganda',
      category: 'Waterfall Uganda',
      description: 'Experience the world\'s most powerful waterfall where the Nile explodes through a narrow gorge. Home to the Big Four wildlife.',
      image: '/images/murchison-falls-hero.jpg',
      activities: ['Big Four Wildlife', 'Falls Boat Trip', 'Game Drives'],
      bestTime: 'Dec-Feb, Jun-Sept',
      duration: '2-3 days'
    },
    {
      name: 'Queen Elizabeth National Park',
      region: 'Western Uganda',
      category: 'Safari Uganda',
      description: 'Uganda\'s most popular park featuring tree-climbing lions, boat safaris on Kazinga Channel, and incredible biodiversity.',
      image: '/images/queen-elizabeth-hero.jpg',
      activities: ['Tree-Climbing Lions', 'Kazinga Channel', 'Game Drives'],
      bestTime: 'Jun-Sept, Dec-Feb',
      duration: '2-4 days'
    },
    {
      name: 'Rwenzori Mountains National Park',
      region: 'Western Uganda',
      category: 'Mountain Uganda',
      description: 'The legendary Mountains of the Moon with Africa\'s third-highest peak. Epic high-altitude trekking through otherworldly landscapes.',
      image: '/images/rwenzori-hero.jpg',
      activities: ['Mountaineering', 'Peak Climbing', 'Glacier Trekking'],
      bestTime: 'Jun-Aug, Dec-Feb',
      duration: '5-12 days'
    },
    {
      name: 'Semuliki Valley National Park',
      region: 'Western Uganda',
      category: 'Rainforest Uganda',
      description: 'East Africa\'s only lowland tropical rainforest with unique Central African species and spectacular hot springs.',
      image: '/images/semuliki-hero.jpg',
      activities: ['Hot Springs', 'Birding', 'Rainforest Walks'],
      bestTime: 'Dec-Feb, Jun-Sept',
      duration: '2-3 days'
    }
  ]

  // Fetch destinations from Payload CMS
  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        setLoading(true)
        const response = await fetch('http://localhost:3001/api/destinations?limit=100')

        if (!response.ok) {
          throw new Error('Failed to fetch destinations')
        }

        const data = await response.json()

        // Transform API data to match component format
        const transformedDestinations = data.docs
          .filter(dest => dest.status === 'published')
          .map(dest => ({
            name: dest.name,
            slug: dest.slug, // Keep the slug from CMS
            region: dest.region === 'western' ? 'Western Uganda' :
              dest.region === 'northern' ? 'Northern Uganda' :
                dest.region === 'eastern' ? 'Eastern Uganda' :
                  dest.region === 'central' ? 'Central Uganda' :
                    dest.region,
            category: dest.category === 'mountain' ? 'Mountain Uganda' :
              dest.category === 'safari' ? 'Safari Uganda' :
                dest.category === 'adventure' ? 'Adventure Uganda' :
                  dest.category === 'primate' ? 'Primate Uganda' :
                    dest.category === 'relaxation' ? 'Relaxation Uganda' :
                      dest.category === 'cultural' ? 'Cultural Uganda' :
                        dest.category,
            description: dest.excerpt || '',
            image: dest.featuredImage?.sizes?.card?.url || dest.featuredImage?.url || 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800',
            activities: dest.highlights?.map(h => h.title) || [],
            bestTime: 'Year-round', // Can be added to CMS schema if needed
            duration: '1-3 days', // Can be added to CMS schema if needed
            source: 'cms' // Mark as CMS destination
          }))

        // Combine CMS destinations with hardcoded destinations
        // Keep existing destinations and add new CMS destinations
        const combinedDestinations = [
          ...hardcodedDestinations.map(d => ({ ...d, source: 'hardcoded' })),
          ...transformedDestinations
        ]

        setDestinations(combinedDestinations)
        setError(null)
      } catch (err) {
        console.error('Error fetching destinations:', err)
        setError(err.message)
        // Fallback to hardcoded destinations on error
        setDestinations(hardcodedDestinations)
      } finally {
        setLoading(false)
      }
    }

    fetchDestinations()
  }, [])

  const getDestinationUrl = (destination) => {
    // If destination has a slug (from CMS), use it
    if (destination.slug) {
      return destination.slug.toLowerCase().replace(/\s+/g, '-')
    }

    // Otherwise use the name mapping for hardcoded destinations
    const destinationName = typeof destination === 'string' ? destination : destination.name
    const urlMap = {
      'Bwindi Impenetrable National Park': 'bwindi-impenetrable',
      'Kibale National Park': 'kibale-national-park',
      'Queen Elizabeth National Park': 'queen-elizabeth',
      'Murchison Falls National Park': 'murchison-falls',
      'Kidepo Valley National Park': 'kidepo-valley',
      'Lake Mburo National Park': 'lake-mburo',
      'Mount Elgon National Park': 'mount-elgon',
      'Rwenzori Mountains National Park': 'rwenzori-mountains',
      'Semuliki Valley National Park': 'semuliki-valley',
      'Jinja': 'jinja',
      'Lake Bunyonyi': 'lake-bunyonyi'
    }
    return urlMap[destinationName] || destinationName.toLowerCase().replace(/\s+/g, '-')
  }

  const regions = [
    {
      name: 'Western Uganda',
      image: '/images/regions/western-uganda-new.jpg'
    },
    {
      name: 'Northern Uganda',
      image: '/images/regions/northern-uganda.jpg'
    },
    {
      name: 'Eastern Uganda',
      image: '/images/regions/eastern-uganda.jpg'
    },
    {
      name: 'Central Uganda',
      image: '/images/regions/central-uganda.jpg'
    },
    {
      name: 'Southern Uganda',
      image: '/images/regions/southern-uganda.jpg'
    }
  ]

  const filteredDestinations = selectedRegion
    ? destinations.filter(dest => dest.region.includes(selectedRegion))
    : destinations

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[500px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/destinations-hero.jpg)',
            filter: 'brightness(0.6)'
          }}
        />
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 font-heading drop-shadow-lg">
            Uganda Safari & Adventure Destinations
          </h1>
          <p className="text-xl md:text-2xl drop-shadow-lg">
            Explore a diverse collection of destinations that combine immersive wildlife safaris with thrilling adventure activities across Uganda's spectacular landscapes
          </p>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-6 text-safari-green font-heading">
            Premier Destinations for Wildlife & Adventure
          </h2>
          <p className="text-lg text-gray-700 text-center max-w-4xl mx-auto leading-relaxed">
            Uganda presents an exceptional array of destinations where classic safari encounters seamlessly blend with active exploration. From high-altitude mountain expeditions to river-based adventures and extensive savannah game drives, our diverse ecosystems provide the perfect backdrop for every type of traveler. Discover landscapes that offer both tranquil wildlife observation and exhilarating outdoor pursuits.
          </p>
        </div>
      </section>

      {/* Discover by Region Section */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-safari-green font-heading">
            Discover by Region
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">
            Click on a region to discover attractions and experiences
          </p>

          <div className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth px-2 max-w-7xl mx-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {regions.map((region, index) => (
              <button
                key={index}
                onClick={() => setSelectedRegion(selectedRegion === region.name ? null : region.name)}
                className={`relative overflow-hidden rounded-xl transition-all duration-300 border-2 flex-shrink-0 ${selectedRegion === region.name
                  ? 'border-safari-green shadow-xl transform scale-105'
                  : 'border-gray-200 hover:border-safari-green hover:shadow-lg'
                  }`}
                style={{ height: '200px', width: '250px' }}
              >
                {/* Background Image */}
                <img
                  src={region.image}
                  alt={region.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className={`absolute inset-0 transition-all duration-300 ${selectedRegion === region.name
                  ? 'bg-safari-green/80'
                  : 'bg-gradient-to-t from-black/60 via-black/20 to-transparent hover:from-safari-green/80 hover:via-safari-green/60 hover:to-safari-green/40'
                  }`}></div>

                {/* Content */}
                <div className="relative z-10 h-full flex items-center justify-center p-6 text-center">
                  <h3 className="text-xl font-bold text-white drop-shadow-lg">
                    {region.name}
                  </h3>
                </div>
              </button>
            ))}
          </div>

          {selectedRegion && (
            <div className="text-center mt-8">
              <button
                onClick={() => setSelectedRegion(null)}
                className="text-safari-green hover:text-safari-gold font-semibold underline"
              >
                Clear Filter - Show All Destinations
              </button>
            </div>
          )}
        </div>
      </section>

      {/* All Destinations Grid Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-safari-green font-heading">
              Uganda's Premier Safari & Adventure Hubs
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Explore our curated selection of top destinations, each offering a unique combination of wildlife encounters, scenic beauty, and active adventure experiences
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-safari-green"></div>
              <p className="text-gray-600 mt-4">Loading destinations...</p>
            </div>
          )}

          {/* Error State (optional - still shows fallback destinations) */}
          {error && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8 max-w-2xl mx-auto">
              <p className="text-yellow-800 text-center">
                <strong>Note:</strong> Showing default destinations. {error}
              </p>
            </div>
          )}

          {/* Destinations Grid */}
          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {filteredDestinations.map((destination, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 flex flex-col"
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={destination.image}
                      alt={destination.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="bg-safari-green text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                        {destination.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-2xl font-bold text-safari-green mb-2 font-heading">
                      {destination.name}
                    </h3>

                    <p className="text-sm text-safari-gold font-semibold mb-3">
                      {destination.region}
                    </p>

                    <p className="text-gray-700 mb-4 leading-relaxed flex-grow">
                      {destination.description}
                    </p>

                    {/* Activity Tags */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {destination.activities.map((activity, i) => (
                        <span
                          key={i}
                          className="bg-safari-light text-safari-green px-3 py-1 rounded-full text-sm font-medium border border-safari-green"
                        >
                          {activity}
                        </span>
                      ))}
                    </div>

                    {/* Explore Button */}
                    <Link
                      to={`/destinations/${getDestinationUrl(destination)}`}
                      className="w-full text-center bg-safari-green text-white py-3 rounded-lg font-semibold hover:bg-safari-gold transition-all duration-300 shadow-md hover:shadow-lg"
                    >
                      Explore {destination.name.split(' ')[0]}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filteredDestinations.length === 0 && (
            <div className="text-center py-12">
              <p className="text-xl text-gray-600">No destinations found in this region.</p>
            </div>
          )}
        </div>
      </section>

      {/* Why Visit Uganda With Us Section */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-safari-green font-heading">
            Why Safari Uganda With Shakes Travel?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300 flex flex-col h-full">
              <div className="relative h-48 overflow-hidden">
                <img
                  src="/images/uganda-safari-specialists.jpg"
                  alt="Uganda Safari Specialists"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8 text-center flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-safari-green mb-4">Uganda Safari Specialists</h3>
                <p className="text-gray-700 leading-relaxed flex-grow">
                  As a Ugandan-owned safari company, we bring unmatched expertise in Uganda's national parks, wildlife behavior, and safari ecosystems—ensuring authentic experiences that showcase our country's natural wonders.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300 flex flex-col h-full">
              <div className="relative h-48 overflow-hidden">
                <img
                  src="/images/conservation-tourism.jpg"
                  alt="Conservation Through Safari Tourism"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8 text-center flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-safari-green mb-4">Conservation Through Safari Tourism</h3>
                <p className="text-gray-700 leading-relaxed flex-grow">
                  Your safari directly supports habitat conservation and community empowerment. Through our Planting Green Paths initiative, every destination visit funds tree planting and ecosystem restoration in the parks you explore.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300 flex flex-col h-full">
              <div className="relative h-48 overflow-hidden">
                <img
                  src="/images/protecting-natural-wonders.jpg"
                  alt="Protecting Natural Wonders"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8 text-center flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-safari-green mb-4">Protecting Natural Wonders</h3>
                <p className="text-gray-700 leading-relaxed flex-grow">
                  We're dedicated to sustainable safari tourism that actively protects Uganda's biodiversity, wildlife habitats, and cultural heritage—ensuring these extraordinary destinations thrive for generations to come.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center" style={{ border: '1px solid #24684f' }}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-safari-green font-heading">
              Start Your Uganda Safari Adventure
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-700">
              Discover Uganda's spectacular safari destinations with a company that puts conservation first. Experience extraordinary wildlife, stunning landscapes, and vibrant cultures while actively protecting the natural wonders you explore.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-2xl mx-auto">
              <button
                onClick={() => handleBooking('Custom Safari Itinerary')}
                className="bg-safari-gold text-white px-8 py-4 rounded-lg font-bold hover:bg-safari-green transition-all duration-300 shadow-lg text-lg w-full sm:w-auto"
              >
                Plan Your Safari
              </button>
              <Link
                to="/stays"
                className="border-2 border-safari-green text-safari-green px-8 py-4 rounded-lg font-bold hover:bg-safari-green hover:text-white transition-all duration-300 shadow-lg text-lg w-full sm:w-auto"
              >
                Search Stays
              </Link>
            </div>
          </div>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedDestination}
      />
    </div>
  )
}
